import { classifyRoom, edgeCrossesDoor, extractRoomNumberIndex, polygonArea, polygonCentroid, } from "./roomNumberingMethods";
import { nextEvenRoomNumber, nextOddRoomNumber } from "./roomNumberingMethods";
const DEBUG = false;
// ---------------------------------------------------------------------------
// Dijkstra: distance from each staircase
// ---------------------------------------------------------------------------
export function buildStaircaseDistanceMap(graph, adj, rooms) {
    const roomTypeMap = new Map(rooms.map((r) => [r.roomNumber, classifyRoom(r.roomName)]));
    const nodesById = new Map(graph.nodes.map((n) => [n.id, n]));
    const staircaseRooms = rooms.filter((r) => classifyRoom(r.roomName) === "staircase");
    const origins = [];
    for (const stair of staircaseRooms) {
        const pts = stair.polygon;
        const cx = pts.reduce((s, p) => s + p[0], 0) / pts.length;
        const cy = pts.reduce((s, p) => s + p[1], 0) / pts.length;
        let bestId = null, bestDist = Infinity;
        for (const node of graph.nodes) {
            if (!(node.rooms ?? []).includes(stair.roomNumber))
                continue;
            const dx = node.posM[0] - cx, dy = node.posM[1] - cy;
            const d = dx * dx + dy * dy;
            if (d < bestDist) {
                bestDist = d;
                bestId = node.id;
            }
        }
        if (bestId)
            origins.push({ nodeId: bestId, staircaseRoomNumber: stair.roomNumber });
    }
    const heap = [];
    const heapPush = (e) => {
        heap.push(e);
        let i = heap.length - 1;
        while (i > 0) {
            const parent = (i - 1) >> 1;
            if (heap[parent][0] <= heap[i][0])
                break;
            [heap[parent], heap[i]] = [heap[i], heap[parent]];
            i = parent;
        }
    };
    const heapPop = () => {
        if (heap.length === 0)
            return undefined;
        const top = heap[0];
        const last = heap.pop();
        if (heap.length > 0) {
            heap[0] = last;
            let i = 0;
            while (true) {
                const l = 2 * i + 1, r = 2 * i + 2;
                let s = i;
                if (l < heap.length && heap[l][0] < heap[s][0])
                    s = l;
                if (r < heap.length && heap[r][0] < heap[s][0])
                    s = r;
                if (s === i)
                    break;
                [heap[i], heap[s]] = [heap[s], heap[i]];
                i = s;
            }
        }
        return top;
    };
    const claimedCorridors = new Map();
    const getNodeCorridors = (nodeId) => (nodesById.get(nodeId)?.rooms ?? []).filter((r) => roomTypeMap.get(r) === "corridor");
    const penaltyCost = new Map();
    const dist = new Map();
    for (const { nodeId, staircaseRoomNumber } of origins) {
        penaltyCost.set(nodeId, 0);
        dist.set(nodeId, { dist: 0, staircaseRoomNumber });
        heapPush([0, nodeId, staircaseRoomNumber, 0]);
    }
    while (heap.length > 0) {
        const [d, nodeId, stairId, euclidSoFar] = heapPop();
        if (d > (penaltyCost.get(nodeId) ?? Infinity))
            continue;
        for (const corridorId of getNodeCorridors(nodeId)) {
            if (!claimedCorridors.has(corridorId))
                claimedCorridors.set(corridorId, stairId);
        }
        for (const { neighbor, cost, distM } of adj.get(nodeId) ?? []) {
            const neighborCorridors = getNodeCorridors(neighbor);
            const blockedByClaim = neighborCorridors.some((cId) => {
                const owner = claimedCorridors.get(cId);
                return owner !== undefined && owner !== stairId;
            });
            if (blockedByClaim)
                continue;
            const nextCost = d + cost;
            if (nextCost < (penaltyCost.get(neighbor) ?? Infinity)) {
                penaltyCost.set(neighbor, nextCost);
                dist.set(neighbor, { dist: euclidSoFar + distM, staircaseRoomNumber: stairId });
                heapPush([nextCost, neighbor, stairId, euclidSoFar + distM]);
            }
        }
    }
    return { distMap: dist, penaltyCostMap: penaltyCost };
}
// ---------------------------------------------------------------------------
// Path tracing helpers
// ---------------------------------------------------------------------------
function traceToStaircase(startId, adj, penaltyCostMap) {
    const path = [startId];
    const visited = new Set([startId]);
    let current = startId;
    const epsilon = 1e-9;
    while (true) {
        const neighbors = adj.get(current) ?? [];
        let bestId = null, minNeighborCost = Infinity;
        const currentCost = penaltyCostMap.get(current) ?? Infinity;
        for (const { neighbor, cost: edgeCost } of neighbors) {
            if (visited.has(neighbor))
                continue;
            const d = penaltyCostMap.get(neighbor) ?? Infinity;
            if (Math.abs(d - (currentCost - edgeCost)) > epsilon)
                continue;
            if (d < minNeighborCost) {
                minNeighborCost = d;
                bestId = neighbor;
            }
        }
        if (bestId === null || minNeighborCost > currentCost + epsilon)
            break;
        path.push(bestId);
        visited.add(bestId);
        current = bestId;
        if (minNeighborCost < epsilon)
            break;
    }
    return path;
}
function detectRoomSide(roomCentroid, torquePath, nodePos) {
    let totalTorque = 0;
    for (let i = 0; i < torquePath.length - 1; i++) {
        const p1 = nodePos.get(torquePath[i]), p2 = nodePos.get(torquePath[i + 1]);
        const dx = p2[0] - p1[0], dy = p2[1] - p1[1];
        const mid = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
        const mx = roomCentroid[0] - mid[0], my = roomCentroid[1] - mid[1];
        const distFromMid = Math.sqrt(mx * mx + my * my);
        const weight = 1 / ((distFromMid + 2) ** 2);
        const cx = roomCentroid[0] - p1[0], cy = roomCentroid[1] - p1[1];
        totalTorque += (dx * cy - dy * cx) * weight;
    }
    return totalTorque > 0 ? "right" : "left";
}
function orderRoomsByAdjacency(rooms, adjTable) {
    const remaining = new Set(rooms.map((r) => r.roomNumber));
    const roomMap = new Map(rooms.map((r) => [r.roomNumber, r]));
    const ordered = [];
    while (remaining.size > 0) {
        let currentId, minDist = Infinity;
        for (const id of remaining) {
            const d = roomMap.get(id).distToStaircase;
            if (d < minDist) {
                minDist = d;
                currentId = id;
            }
        }
        while (currentId) {
            remaining.delete(currentId);
            ordered.push(roomMap.get(currentId));
            const neighbors = adjTable.get(currentId) || new Set();
            let nextId, nextMinDist = Infinity;
            for (const neighborId of neighbors) {
                if (remaining.has(neighborId)) {
                    const d = roomMap.get(neighborId)?.distToStaircase;
                    if (d !== undefined && d < nextMinDist) {
                        nextMinDist = d;
                        nextId = neighborId;
                    }
                }
            }
            currentId = nextId;
        }
    }
    return ordered;
}
// ---------------------------------------------------------------------------
// Room hierarchy
// ---------------------------------------------------------------------------
export function buildRoomHierarchy(graph, adj, distMap, penaltyCostMap, rooms, doors = []) {
    const nodesById = new Map(graph.nodes.map((n) => [n.id, n]));
    const nodePos = new Map(graph.nodes.map((n) => [n.id, [n.posM[0], n.posM[1]]]));
    const hierarchy = {};
    for (const room of rooms) {
        hierarchy[room.roomNumber] = {
            sub_rooms: [],
            parent_room: null,
            door_to_parent: false,
            size: polygonArea(room.polygon),
            center: polygonCentroid(room.polygon),
        };
    }
    for (const room of rooms) {
        let bestNodeId = null, bestDist = Infinity;
        for (const node of graph.nodes) {
            if (!(node.rooms ?? []).includes(room.roomNumber))
                continue;
            const d = distMap.get(node.id)?.dist ?? Infinity;
            if (d < bestDist) {
                bestDist = d;
                bestNodeId = node.id;
            }
        }
        if (bestNodeId === null || bestDist === Infinity)
            continue;
        const path = traceToStaircase(bestNodeId, adj, penaltyCostMap);
        let parentRoom = null;
        let viaEdgeA = null, viaEdgeB = null;
        for (let i = 1; i < path.length; i++) {
            const nodeRooms = nodesById.get(path[i])?.rooms ?? [];
            const otherRoom = nodeRooms.find((r) => r !== room.roomNumber);
            if (otherRoom !== undefined) {
                parentRoom = otherRoom;
                viaEdgeA = nodePos.get(path[i - 1]) ?? null;
                viaEdgeB = nodePos.get(path[i]) ?? null;
                break;
            }
        }
        if (parentRoom === null || !hierarchy[parentRoom])
            continue;
        const viasDoor = viaEdgeA !== null && viaEdgeB !== null && doors.length > 0 &&
            doors.some((door) => edgeCrossesDoor(viaEdgeA, viaEdgeB, door));
        hierarchy[room.roomNumber].parent_room = parentRoom;
        hierarchy[room.roomNumber].door_to_parent = viasDoor;
        hierarchy[parentRoom].sub_rooms.push(room.roomNumber);
    }
    return hierarchy;
}
// ---------------------------------------------------------------------------
// Room number assignment
// ---------------------------------------------------------------------------
const RESET_NAMES = true;
export function assignRoomNumbers(graph, adj, distMap, penaltyCostMap, rooms, doors, roomAdjTable, floorNumber = 0) {
    if (RESET_NAMES)
        rooms.forEach((room) => { if (!room.roomName)
            room.roomName = "-"; });
    const roomTypeMap = new Map(rooms.map((r) => [r.roomNumber, classifyRoom(r.roomName)]));
    const roomAreaMap = new Map(rooms.map((r) => [r.roomNumber, polygonArea(r.polygon)]));
    const hierarchy = buildRoomHierarchy(graph, adj, distMap, penaltyCostMap, rooms, doors);
    const SMALL_CORRIDOR_AREA_M2 = 10;
    const isEligible = (r) => {
        const type = roomTypeMap.get(r.roomNumber);
        if (type === "numbered_corridor" && (roomAreaMap.get(r.roomNumber) ?? 0) >= SMALL_CORRIDOR_AREA_M2)
            return false;
        if (type !== "room" && type !== "numbered_corridor")
            return false;
        const parentRoomNumber = hierarchy[r.roomNumber]?.parent_room;
        if (!parentRoomNumber)
            return false;
        const parentType = roomTypeMap.get(parentRoomNumber);
        const parentArea = roomAreaMap.get(parentRoomNumber) ?? 0;
        return parentType === "staircase" || (parentType === "corridor" && parentArea >= SMALL_CORRIDOR_AREA_M2);
    };
    const nodesById = new Map(graph.nodes.map((n) => [n.id, n]));
    const nodePos = new Map(graph.nodes.map((n) => [n.id, [n.posM[0], n.posM[1]]]));
    const staircaseRooms = rooms
        .filter((r) => classifyRoom(r.roomName) === "staircase")
        .map((r) => { const c = polygonCentroid(r.polygon); return { r, proj: c[0] + c[1] }; })
        .sort((a, b) => a.proj - b.proj)
        .map(({ r }) => r);
    const allCentroids = rooms.map((r) => polygonCentroid(r.polygon));
    const count = allCentroids.length || 1;
    const floorCentroid = [
        allCentroids.reduce((sum, c) => sum + (c[0] ?? 0), 0) / count,
        allCentroids.reduce((sum, c) => sum + (c[1] ?? 0), 0) / count,
    ];
    const staircasePrefix = new Map(staircaseRooms.map((r) => {
        const building = r.building?.trim() ?? "";
        return [r.roomNumber, building];
    }));
    const results = [];
    for (const room of rooms) {
        if (!isEligible(room))
            continue;
        let bestNodeId = null, bestDist = Infinity;
        for (const node of graph.nodes) {
            if (!(node.rooms ?? []).includes(room.roomNumber))
                continue;
            const d = distMap.get(node.id)?.dist ?? Infinity;
            if (d < bestDist) {
                bestDist = d;
                bestNodeId = node.id;
            }
        }
        const staircaseRoomNumber = bestNodeId ? distMap.get(bestNodeId)?.staircaseRoomNumber : null;
        if (bestNodeId === null || bestDist === Infinity) {
            if (DEBUG)
                console.log(`[RoomNumbering] SKIP ${room.roomNumber} (${room.roomName ?? "?"}) — no reachable node in distMap`);
            continue;
        }
        const roomNodeDists = graph.nodes
            .filter((n) => (n.rooms ?? []).includes(room.roomNumber))
            .map((n) => ({ id: n.id, dist: distMap.get(n.id)?.dist ?? Infinity }))
            .sort((a, b) => a.dist - b.dist);
        const path = traceToStaircase(bestNodeId, adj, penaltyCostMap);
        if (path.length === 0)
            continue;
        let targetCorridorId = null;
        for (let i = 0; i < path.length; i++) {
            const nodeRooms = nodesById.get(path[i])?.rooms ?? [];
            const corridor = nodeRooms.find((r) => roomTypeMap.get(r) === "corridor");
            if (corridor) {
                targetCorridorId = corridor;
                break;
            }
        }
        if (!targetCorridorId || !staircaseRoomNumber)
            continue;
        let startNodeId = null, maxD = -1;
        for (const node of graph.nodes) {
            if (!(node.rooms ?? []).includes(targetCorridorId))
                continue;
            const entry = distMap.get(node.id);
            if (entry && entry.staircaseRoomNumber === staircaseRoomNumber && entry.dist !== Infinity && entry.dist > maxD) {
                maxD = entry.dist;
                startNodeId = node.id;
            }
        }
        if (!startNodeId)
            continue;
        const torquePath = traceToStaircase(startNodeId, adj, penaltyCostMap);
        const centroid = polygonCentroid(room.polygon);
        if (torquePath.length < 2)
            continue;
        const side = detectRoomSide(centroid, torquePath, nodePos);
        if (DEBUG)
            console.log(`[RoomNumbering] ${room.roomNumber} (${room.roomName ?? "?"})` +
                ` → staircase: ${staircaseRoomNumber}, side: ${side}, dist: ${bestDist.toFixed(2)}, torquePath: ${torquePath.length}` +
                ` | chosen node: ${bestNodeId}` +
                ` | node dists: [${roomNodeDists.slice(0, 5).map((n) => `${n.id}=${n.dist.toFixed(1)}`).join(", ")}${roomNodeDists.length > 5 ? "…" : ""}]`);
        results.push({ roomNumber: room.roomNumber, calculatedRoomNumber: "", staircaseRoomNumber, side, distToStaircase: bestDist, centroid });
    }
    const output = {};
    const floorStr = floorNumber.toString().padStart(2, "0");
    const roomByRoomNumber = new Map(rooms.map((r) => [r.roomNumber, r]));
    const byBuilding = new Map();
    for (const r of results) {
        const ownBuilding = roomByRoomNumber.get(r.roomNumber)?.building?.trim();
        const prefix = (ownBuilding != null && ownBuilding !== "")
            ? ownBuilding
            : (staircasePrefix.get(r.staircaseRoomNumber) ?? "?");
        if (!byBuilding.has(prefix))
            byBuilding.set(prefix, []);
        byBuilding.get(prefix).push(r);
    }
    for (const [prefix, roomResults] of byBuilding) {
        const getManualIndex = (roomNumber) => {
            const manual = roomByRoomNumber.get(roomNumber)?.manualRoomNumber?.trim();
            if (!manual || !manual.toUpperCase().startsWith(prefix.toUpperCase()))
                return null;
            return extractRoomNumberIndex(manual);
        };
        const usedEven = new Set(), usedOdd = new Set();
        for (const result of roomResults) {
            const idx = getManualIndex(result.roomNumber);
            if (idx == null)
                continue;
            if (idx % 2 === 0)
                usedEven.add(idx);
            else
                usedOdd.add(idx);
        }
        const assignSide = (ordered, start, nextFn, used) => {
            let last = start;
            for (const r of ordered) {
                const manual = roomByRoomNumber.get(r.roomNumber)?.manualRoomNumber?.trim();
                if (manual) {
                    output[r.roomNumber] = manual;
                    last = extractRoomNumberIndex(manual) ?? last;
                    if (DEBUG)
                        console.log(`[RoomNumbering] MANUAL ${r.roomNumber} → ${manual}`);
                }
                else {
                    const num = nextFn(last, used);
                    used.add(num);
                    last = num;
                    const tag = `${prefix}${floorStr}.${num.toString().padStart(3, "0")}`;
                    output[r.roomNumber] = tag;
                    if (DEBUG)
                        console.log(`[RoomNumbering] ASSIGNED ${r.roomNumber} → ${tag} (dist=${r.distToStaircase.toFixed(2)})`);
                }
            }
        };
        const rightRooms = roomResults.filter((r) => { const idx = getManualIndex(r.roomNumber); return idx != null ? idx % 2 === 0 : r.side === "right"; });
        const leftRooms = roomResults.filter((r) => { const idx = getManualIndex(r.roomNumber); return idx != null ? idx % 2 !== 0 : r.side === "left"; });
        assignSide(orderRoomsByAdjacency(rightRooms, roomAdjTable), 0, nextEvenRoomNumber, usedEven);
        assignSide(orderRoomsByAdjacency(leftRooms, roomAdjTable), -1, nextOddRoomNumber, usedOdd);
    }
    const SUFFIX_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    // Returns true if a manual number already carries a suffix letter (e.g. "A-bygg01.002A")
    const hasManualSuffix = (roomNumber) => /\.\d+[A-Z]$/i.test(roomByRoomNumber.get(roomNumber)?.manualRoomNumber?.trim() ?? "");
    const assignedByBase = Object.entries(output).filter(([roomNumber]) => results.some((r) => r.roomNumber === roomNumber));
    for (const [baseRoomNumber, baseTag] of assignedByBase) {
        const queue = [...(hierarchy[baseRoomNumber]?.sub_rooms ?? [])];
        if (queue.length === 0)
            continue;
        const visited = new Set([baseRoomNumber]);
        // Determine the unsuffixed tag base and what letter the base room gets.
        // If the user already set a manual suffix (e.g. "A-bygg01.002A"), honour it
        // and derive the tag base by stripping that letter so children continue from B.
        let tagBase;
        if (hasManualSuffix(baseRoomNumber)) {
            // base already has its letter — strip it to get the shared tag base
            tagBase = output[baseRoomNumber].replace(/[A-Z]$/i, "");
            // leave output[baseRoomNumber] as the manual value
        }
        else {
            tagBase = baseTag;
            output[baseRoomNumber] = `${tagBase}${SUFFIX_LETTERS[0]}`; // auto: base gets A
        }
        let suffixIdx = 1; // children start from B
        while (queue.length > 0) {
            const current = queue.shift();
            if (visited.has(current))
                continue;
            visited.add(current);
            const entry = hierarchy[current];
            if (!entry)
                continue;
            if (hasManualSuffix(current)) {
                // User explicitly set a suffix letter — use it as-is, don't consume an auto slot
                output[current] = roomByRoomNumber.get(current).manualRoomNumber.trim();
            }
            else if (output[current] === undefined || output[current] === "-") {
                output[current] = `${tagBase}${SUFFIX_LETTERS[suffixIdx % 26] ?? "?"}`;
                suffixIdx++;
            }
            for (const sub of entry.sub_rooms) {
                if (!visited.has(sub))
                    queue.push(sub);
            }
        }
    }
    if (RESET_NAMES) {
        for (const room of rooms) {
            if (output[room.roomNumber] === undefined)
                output[room.roomNumber] = "-";
        }
    }
    return output;
}
